import React, { useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { HeaderBlock, Expander, ExpanderContent, Button, useAtSize, Theme, Separator, Grid, Cell } from '@fs/zion-ui'
import ZionForm, { useZionForm, FormTextField, useZionFormContext } from '@fs/zion-form'
import { ArrowCaret } from '@fs/zion-icon'
import { i18n } from '@fs/zion-locale'
import type { SearchParams } from '../../types/data-types'
import sendAnalytics from '../../services/analytics-helper'
import EventFields from './EventFields'

const inputContainerCss = css`
  text-align: left;
`

const nameFieldSwapLocalesRegEx = /^ja|^km|^ko|^mn|^ro|^to|^vi|^zh/

function SearchButton(): JSX.Element {
  const [t] = useTranslation()
  const atSize = useAtSize()
  const isMobile = atSize({ md: false, default: true })
  const { formState } = useZionFormContext()
  const { isValid, isDirty } = formState
  return (
    <Button
      type="submit"
      emphasis="high"
      fullWidth={isMobile}
      disabled={!isValid || !isDirty}
      data-testid="search-button"
    >
      {t('common-ui:search.action')}
    </Button>
  )
}

export default function Search({
  header,
  params,
  onSubmit,
  showMore,
  analytics,
  onSearchPage = false,
}: {
  header?: string | undefined
  params?: SearchParams
  onSubmit?: (values: SearchParams) => void
  showMore?: boolean
  analytics?: Record<string, string>
  onSearchPage?: boolean
}): JSX.Element {
  const [t] = useTranslation()
  const atSize = useAtSize()
  const isMobile = atSize({ md: false, default: true })
  const methods = useZionForm({
    mode: 'all',
    resolver: validate,
    defaultValues: params,
  })
  const swapNames = nameFieldSwapLocalesRegEx.test(i18n.language)
  const [open, setOpen] = useState(false)

  function validate(values: SearchParams): { values: unknown; errors: unknown } {
    const error = !values.self?.surname?.value
    return {
      values: error ? {} : values,
      errors: error ? { self: { surname: { value: { message: t('ancestors.fields.required') } } } } : {},
    }
  }

  function handleSubmit(values: SearchParams): void {
    if (onSubmit) {
      onSubmit(values)
    }
    sendAnalytics({ ...analytics, link_name: 'Search' })
  }

  return (
    <div css={inputContainerCss}>
      <ZionForm methods={methods} onSubmit={handleSubmit}>
        {header && <HeaderBlock centered size="sm" heading={header} />}

        <NameFields swapNames={swapNames} onSearchPage={onSearchPage} />

        {showMore && (
          <>
            <Expander open={open} setOpen={setOpen}>
              <ExpanderContent>
                <EventFields name="birth" type="birth" />
                <EventFields name="death" type="death" />
              </ExpanderContent>
            </Expander>
            <Separator size="xxs" />

            <Grid>
              <Cell columns={isMobile ? 'hide' : 2} />
              <Button
                columns={isMobile ? 12 : 10}
                fullWidth={isMobile}
                data-testid="show-more-less-button"
                onClick={(event) => {
                  setOpen(!open)
                  event.target.blur()
                  sendAnalytics({ ...analytics, link_name: open ? 'Less Search Options' : 'More Search Options' })
                }}
                Icon={ArrowCaret}
                iconDirection={open ? 'up' : 'down'}
              >
                {open ? t('ancestors.less-search-options.action') : t('ancestors.more-search-options.action')}
              </Button>
              {isMobile && <SearchButton />}
            </Grid>
          </>
        )}
      </ZionForm>
    </div>
  )
}

function NameFields({ swapNames, onSearchPage = false }): JSX.Element {
  const [t] = useTranslation()
  const atSize = useAtSize()
  const isMobile = atSize({ md: false, default: true })

  return (
    <Grid noHorizontalGutters>
      <Cell columns={isMobile ? 12 : 2} />
      {swapNames ? (
        <FormTextField
          columns={isMobile ? 12 : 4}
          data-testid="search-surname"
          label={t('ancestors.surname.label')}
          name="self.surname.value"
        />
      ) : (
        <FormTextField
          columns={isMobile ? 12 : 4}
          data-testid="search-given-name"
          label={t('ancestors.given-name.label')}
          name="self.given.value"
        />
      )}
      {swapNames ? (
        <FormTextField
          columns={isMobile ? 12 : 4}
          data-testid="search-given-name"
          label={t('ancestors.given-name.label')}
          name="self.given.value"
        />
      ) : (
        <FormTextField
          columns={isMobile ? 12 : 4}
          data-testid="search-surname"
          label={t('ancestors.surname.label')}
          name="self.surname.value"
        />
      )}

      {/* Without Theme here, WhiteText in HomePageHeader makes the button white, which looks odd in this situation. Can't use BillboardButton because it is too tall. */}
      {!(isMobile && onSearchPage) && (
        <Theme alignY="nextToInput" columns={isMobile ? 12 : 2} theme="day" transparent>
          <SearchButton />
        </Theme>
      )}
    </Grid>
  )
}
